import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, CheckCircle2, Mail } from "lucide-react";
import { MaskedLines, Reveal, ChapterHeading } from "@/components/motion";
import JsonLd from "@/components/JsonLd";
import { EMAIL } from "@/constants/site";
import { MODALITIES } from "@/constants/modalities";
import { usePageMeta } from "@/hooks/usePageMeta";

const INCLUDES = [
  { title: "Workload & usage assumptions", body: "Weekly patient volume, projections, and techniques for each room — the numbers every calculation stands on." },
  { title: "Occupancy & area classification", body: "Which surrounding areas are controlled vs. uncontrolled, and who occupies them — offices, corridors, waiting rooms." },
  { title: "Barrier thickness schedule", body: "Wall-by-wall shielding callouts in lead equivalency, gypsum, or concrete — ready for your contractor to build from." },
  { title: "Scatter, leakage & primary calcs", body: "NCRP Report No. 147 methodology, shown transparently so your state reviewer can follow every step." },
  { title: "Marked-up floor plans", body: "Your drawings, annotated with barriers, doors, and any details that need attention during construction." },
  { title: "Physicist signature & credentials", body: "Prepared, reviewed, and signed directly by an approved consulting physicist — not stamped by a vendor." },
];

const WHEN = [
  { title: "New construction or renovation", body: "Most states require a physicist's shielding plan before walls go up — retrofitting lead after the fact is the expensive path." },
  { title: "New or replacement equipment", body: "A new CT, fluoroscopy unit, or CBCT changes workload and scatter — your existing plan may no longer cover the room." },
  { title: "State registration or plan review", body: "Many state radiation control programs require the shielding plan on file — or submitted for review — before the room can be used." },
  { title: "Room repurposing", body: "Turning a general x-ray room into a CT suite (or vice versa) almost always changes the shielding requirements." },
];

const FAQS = [
  {
    q: "Is a shielding plan the same thing as a shielding design?",
    a: "Yes — the terms are used interchangeably. Both refer to the physicist-prepared report that specifies barrier thicknesses for your room. Some states say 'shielding plan,' others say 'shielding design' or 'shielding evaluation.' What matters is that it's signed by a qualified physicist and follows NCRP 147 methodology.",
  },
  {
    q: "Who is allowed to prepare and sign a shielding plan?",
    a: "Requirements vary by state, but most expect a qualified medical or health physicist — typically board-certified or state-approved. Every Heartland Physics plan is prepared and signed directly by Lancer Smith, M.S., an approved consulting physicist.",
  },
  {
    q: "How much does a shielding plan cost, and how long does it take?",
    a: "$950 per room, flat. Most plans are delivered within five business days of receiving complete project information — facility details, floor plans, and equipment specs through our online intake.",
  },
  {
    q: "Do small offices — dental, veterinary, chiropractic — need a shielding plan too?",
    a: "In most states, yes. Any installation of x-ray producing equipment typically requires a shielding evaluation before registration. Dental CBCT and veterinary radiography rooms are among the most common projects we handle.",
  },
  {
    q: "What happens if my state reviewer questions the plan?",
    a: "You get direct support. If a state agency or inspector has questions about the calculations or documentation, we respond to them directly — revisions related to plan review are included.",
  },
];

const ShieldingPlan = () => {
  usePageMeta(
    "Shielding Plan for X-Ray, CT & Imaging Rooms | Heartland Physics",
    "What a shielding plan includes, when you need one, and how to get a physicist-signed radiation shielding plan for your x-ray, CT, dental, or veterinary room — $950 per room, five-business-day turnaround."
  );
  return (
    <>
      <JsonLd
        id="service-shielding-plan"
        data={{
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Radiation Shielding Plan",
          description: "Physicist-prepared radiation shielding plans for diagnostic imaging rooms — barrier thickness calculations, workload assumptions, and state-ready documentation.",
          provider: { "@type": "ProfessionalService", name: "Heartland Physics", url: "https://heartlandphysics.com/" },
          areaServed: "United States",
          offers: { "@type": "Offer", price: "950", priceCurrency: "USD" },
        }}
      />
      <JsonLd
        id="faq-shielding-plan"
        data={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }}
      />

      <section data-testid="shielding-plan-hero" className="border-b border-border bg-blueprint-grid pt-16">
        <div className="mx-auto max-w-7xl px-5 pb-14 pt-14 md:px-8 md:pb-20 md:pt-20">
          <Reveal y={12}>
            <p className="eyebrow">Shielding Plan</p>
          </Reveal>
          <h1 className="mt-6 max-w-3xl font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-6xl">
            <MaskedLines delay={0.1} lines={["A shielding plan your state", "accepts the first time."]} />
          </h1>
          <Reveal delay={0.5}>
            <p className="mt-7 max-w-2xl text-base leading-relaxed text-ink/70 md:text-lg" data-testid="shielding-plan-intro">
              A shielding plan is the physicist-signed document that proves your x-ray, CT, or imaging room
              protects staff and the public — and most states won't let you build, register, or operate
              without one. Heartland Physics prepares yours in five business days, for $950 per room.
            </p>
          </Reveal>
          <Reveal delay={0.65} className="mt-9 flex flex-wrap items-center gap-4">
            <Link
              to="/request"
              data-testid="shielding-plan-cta-request"
              className="group flex items-center gap-2 bg-blueprint px-7 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-ink"
            >
              Request your shielding plan
              <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <a
              href={`mailto:${EMAIL}`}
              data-testid="shielding-plan-cta-email"
              className="group flex items-center gap-2 border border-ink/25 bg-white/60 px-7 py-4 text-sm font-semibold text-ink transition-colors duration-200 hover:border-blueprint hover:text-blueprint"
            >
              <Mail size={16} /> Email Lancer@HeartlandPhysics.com
            </a>
          </Reveal>
        </div>
      </section>

      <section className="border-b border-border bg-white" data-testid="shielding-plan-what">
        <div className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24">
          <ChapterHeading index="01" eyebrow="What it is" title="What a shielding plan actually is" />
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-3xl text-base leading-relaxed text-ink/70 md:text-lg">
              Before an imaging room is built or new equipment goes in, someone has to answer a precise
              physics question: <span className="font-semibold text-ink">how thick do the walls, doors, and windows need to be?</span> A
              shielding plan answers it with math — workload, use and occupancy factors, scatter and leakage
              calculations under NCRP Report No. 147 — and documents the answer in a report your state
              radiation control program can review. It protects your patients, your staff, and your license.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-px border border-border bg-border sm:grid-cols-2 lg:grid-cols-3">
            {INCLUDES.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.05} className="h-full">
                <div className="flex h-full flex-col bg-white p-6">
                  <CheckCircle2 size={18} className="text-blueprint" />
                  <h3 className="mt-4 font-heading text-base font-bold tracking-tight text-ink">{c.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink/60">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-border bg-paper" data-testid="shielding-plan-when">
        <div className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24">
          <ChapterHeading index="02" eyebrow="Timing" title="When you need one" />
          <div className="mt-12 grid gap-px border border-border bg-border sm:grid-cols-2">
            {WHEN.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.06} className="h-full">
                <div className="h-full bg-white p-7">
                  <p className="font-mono text-xs text-blueprint">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="mt-3 font-heading text-base font-bold tracking-tight text-ink">{w.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink/60">{w.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-3xl text-sm leading-relaxed text-ink/60">
              Not sure whether your state requires a formal plan review? Check your{" "}
              <Link to="/shielding-requirements" className="font-semibold text-blueprint hover:text-ink">state's shielding requirements</Link>
              {" "}— we track the agency, the forms, and the timelines for all 50 states.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-b border-border bg-white" data-testid="shielding-plan-faq">
        <div className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24">
          <ChapterHeading index="03" eyebrow="Questions" title="Shielding plan FAQ" />
          <div className="mt-10 max-w-3xl divide-y divide-border border-y border-border">
            {FAQS.map((f, i) => (
              <Reveal key={f.q} delay={i * 0.05}>
                <div className="py-6" data-testid={`shielding-plan-faq-${i}`}>
                  <h3 className="font-heading text-base font-bold tracking-tight text-ink">{f.q}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-ink/65">{f.a}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-border bg-paper" data-testid="shielding-plan-modalities">
        <div className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-20">
          <ChapterHeading index="04" eyebrow="By modality" title="Shielding plans for every room type" />
          <div className="mt-10 grid gap-px border border-border bg-border sm:grid-cols-2 lg:grid-cols-4">
            {MODALITIES.slice(0, 4).map((m, i) => (
              <Reveal key={m.slug} delay={i * 0.05} className="h-full">
                <Link to={`/shielding-design/${m.slug}`} data-testid={`shielding-plan-modality-${m.slug}`} className="group flex h-full flex-col bg-white p-6 transition-colors duration-300 hover:bg-ink">
                  <h3 className="font-heading text-sm font-bold tracking-tight text-ink transition-colors group-hover:text-white">{m.name}</h3>
                  <span className="mt-4 flex items-center gap-1.5 text-xs font-semibold text-blueprint transition-colors group-hover:text-azure">
                    Requirements & process <ArrowRight size={13} className="transition-transform duration-200 group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-6 text-sm text-ink/60">
              Also:{" "}
              {MODALITIES.slice(4).map((m, i) => (
                <span key={m.slug}>
                  {i > 0 && " · "}
                  <Link to={`/shielding-design/${m.slug}`} className="font-semibold text-blueprint hover:text-ink">{m.name}</Link>
                </span>
              ))}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-ink bg-blueprint-grid-dark" data-testid="shielding-plan-final-cta">
        <div className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24">
          <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-end">
            <div>
              <p className="eyebrow !text-azure">Flat rate · 5 business days</p>
              <h2 className="mt-4 max-w-xl font-heading text-3xl font-extrabold tracking-tight text-white md:text-4xl">
                <MaskedLines lines={["Your room. Your plan.", "$950, signed by the physicist."]} />
              </h2>
            </div>
            <Reveal delay={0.2} className="flex flex-wrap gap-4">
              <Link
                to="/request"
                data-testid="shielding-plan-final-request"
                className="group flex items-center gap-2 bg-white px-7 py-4 text-sm font-semibold text-ink transition-colors duration-200 hover:bg-azure hover:text-white"
              >
                Start your request
                <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <a
                href={`mailto:${EMAIL}`}
                data-testid="shielding-plan-final-email"
                className="flex items-center gap-2 border border-white/30 px-7 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:border-azure hover:text-azure"
              >
                <Mail size={16} /> Email Lancer@HeartlandPhysics.com
              </a>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
};

export default ShieldingPlan;
